
import React from 'react';
import { Button } from '@/components/ui/button';
import { Download, Loader2, Play, RefreshCw } from 'lucide-react';
import { Project } from '@/hooks/useProjects';
import { useVideoExport } from '@/hooks/useVideoExport';
import { useStorageDownload } from '@/hooks/useStorageDownload';
import { useQueryClient } from '@tanstack/react-query';

interface VideoExportPanelProps {
  project: Project;
}

const VideoExportPanel = ({ project }: VideoExportPanelProps) => {
  const { exportVideo, isExporting } = useVideoExport();
  const { createSignedUrl } = useStorageDownload();
  const queryClient = useQueryClient();

  const handleExport = () => {
    exportVideo(project.id);
  };

  const handleRefreshStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['project', project.id] });
  };

  const handleDownload = async () => {
    if (!project.exported_video_storage_path) return;
    
    try {
      const signedUrl = await createSignedUrl('exported-videos', project.exported_video_storage_path, 3600);
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = `${project.title}_export.webm`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('❌ Download failed:', error);
    }
  };

  const getStatusDisplay = () => {
    switch (project.export_status) {
      case 'none':
        return null;
      case 'queued':
        return (
          <div className="flex items-center space-x-2 text-sm text-yellow-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Export queued...</span>
          </div>
        );
      case 'processing':
        return (
          <div className="flex items-center space-x-2 text-sm text-blue-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Processing video...</span>
          </div>
        );
      case 'completed':
        return (
          <div className="flex items-center space-x-2 text-sm text-green-600">
            <Play className="h-4 w-4" />
            <span>Export completed</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-2 text-sm text-red-600">
              <span>Export failed</span>
            </div>
            {project.export_last_error && (
              <div className="text-xs text-gray-500">
                {project.export_last_error}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  const getButtonText = () => {
    switch (project.export_status) {
      case 'none':
      case 'failed':
        return 'Export Video';
      case 'queued':
        return 'Export Queued';
      case 'processing':
        return 'Processing...';
      case 'completed':
        return 'Re-export Video';
      default:
        return 'Export Video';
    }
  };

  const isButtonDisabled = () => {
    return isExporting || project.export_status === 'queued' || project.export_status === 'processing';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <Button
          onClick={handleExport}
          disabled={isButtonDisabled()}
          className="flex items-center space-x-2"
        >
          {isExporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Play className="h-4 w-4" />
          )}
          <span>{getButtonText()}</span>
        </Button>

        {project.export_status === 'completed' && project.exported_video_storage_path && (
          <Button onClick={handleDownload} variant="outline" className="flex items-center space-x-2">
            <Download className="h-4 w-4" />
            <span>Download</span>
          </Button>
        )}

        <Button
          onClick={handleRefreshStatus}
          variant="ghost"
          size="sm"
          className="flex items-center space-x-1"
        >
          <RefreshCw className="h-3 w-3" />
          <span className="text-xs">Refresh</span>
        </Button>
      </div>

      {getStatusDisplay()}
    </div>
  );
};

export default VideoExportPanel;
